import { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Keeps a fixed-width factsheet preview (e.g. SQUAD 794px / P&R 900px) inside the
 * right column by scaling it down when the column is narrower.
 * Do not wrap fluid (width:100%) templates — max-content shrink-wraps them.
 */
export const FitPreviewWidth: FC<{ children: ReactNode }> = ({ children }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [layoutHeight, setLayoutHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const host = hostRef.current;
    const content = contentRef.current;
    if (!host || !content) return;

    const update = () => {
      // offsetWidth/scrollHeight are layout sizes (pre-transform).
      const natural = Math.max(content.scrollWidth, content.offsetWidth);
      const available = host.clientWidth;
      if (!natural || !available) return;
      const next = Math.min(1, available / natural);
      setScale(next);
      setLayoutHeight(Math.ceil(content.scrollHeight * next));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(host);
    ro.observe(content);
    return () => ro.disconnect();
  }, [children]);

  return (
    <Box ref={hostRef} sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Box sx={{ height: layoutHeight, overflow: 'hidden', width: '100%' }}>
        <Box
          ref={contentRef}
          sx={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: 'max-content',
            maxWidth: 'none',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
